import { ScrollArea } from "@ui/components/ScrollArea";
import { useSheetSurface } from "@ui/components/SheetSurface";
import { useScrollSentinel } from "@ui/hooks/use-scroll-sentinel";
import { ScrollBox } from "@ui/primitives/Scroll";
import { type ReactNode, useEffect, useRef } from "react";
import { surfaceBody, surfaceBodyDesktop } from "./sheet-chrome.styles";

export interface SurfaceBodyProps {
  /** Scrollable content rendered between header and footer */
  children: ReactNode;
  /** Vertically center content on mobile. Use for short, non-scrollable content like confirmations. */
  centered?: boolean;
  className?: string;
}

/**
 * Scrollable content area between header and footer.
 *
 * On mobile it must be a Silk scroll container so one swipe hands off between
 * sheet travel and content scroll (at scroll top, swiping down dismisses). On
 * desktop there is no travel gesture, so the body is a ScrollArea instead —
 * the design-system rail rather than the native scrollbar Silk keeps.
 */
export function SurfaceBody({ children, centered, className }: SurfaceBodyProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const { setIsAtBottom, hasFooter, platform } = useSheetSurface();

  const [sentinelRef, isAtBottom] = useScrollSentinel({
    root: bodyRef,
    threshold: 0,
  });

  useEffect(() => {
    setIsAtBottom(isAtBottom);
  }, [isAtBottom, setIsAtBottom]);

  const sentinel = <div ref={sentinelRef} className="h-px w-full shrink-0" />;

  if (platform === "desktop") {
    const styles = surfaceBodyDesktop({ footerless: !hasFooter });
    return (
      <ScrollArea className={styles.root({ className })} viewportClassName={styles.viewport()} viewportRef={bodyRef}>
        {children}
        {sentinel}
      </ScrollArea>
    );
  }

  return (
    <ScrollBox
      ref={bodyRef}
      className={surfaceBody({ centered, className, footerless: !hasFooter })}
      contentClassName="w-full"
    >
      {children}
      {sentinel}
    </ScrollBox>
  );
}
